import { z } from "zod";

/**
 * Kanonik parola politikası (CLOS-331 · ADR-0017).
 *
 * Türkçe alfabe dahil: `[A-Z]`/`[a-z]` Türkçe harfleri kaçırır ve Türkçe bir parola
 * "büyük harf yok" diye reddedilirdi.
 *
 * Uzunluk 8 → **10**: uzunluk, karakter çeşidinden daha çok iş görür. Rakam kuralı
 * eklendi ki üç sınıf birden zorunlu olsun.
 */
export const PASSWORD_MIN_LENGTH = 10;

/**
 * ÜST SINIR SAĞLAYICIDAN GELİYOR (CLOS-630).
 *
 * ⚠ Şemada üst sınır YOKTU: 128 karakterden uzun bir parola formdan geçiyor, sağlayıcı
 * onu `PASSWORD_TOO_LONG` ile reddediyor ve kullanıcı ancak sunucudan dönen genel bir
 * hatayı görüyordu. Kullanıcının anında düzeltebileceği bir girdi hatası, ağ turuna ve
 * anlaşılmaz bir cevaba dönüşüyordu.
 *
 * Sınır BetterAuth'un kendi sınırı; burada tekrarlanması bilinçli: kural kullanıcının
 * yazdığı yerde, forma bastığı anda söylenmeli.
 */
export const PASSWORD_MAX_LENGTH = 128;

export const passwordSchema = z
  .string({ error: "Şifre zorunludur" })
  .min(PASSWORD_MIN_LENGTH, `Şifre en az ${PASSWORD_MIN_LENGTH} karakter olmalıdır`)
  .max(PASSWORD_MAX_LENGTH, `Şifre en fazla ${PASSWORD_MAX_LENGTH} karakter olabilir`)
  .regex(/[A-ZÇĞİÖŞÜ]/, "Şifre en az bir büyük harf içermelidir")
  .regex(/[a-zçğıöşü]/, "Şifre en az bir küçük harf içermelidir")
  .regex(/\d/, "Şifre en az bir rakam içermelidir");

/**
 * E-posta **normalize edilir**: baştaki/sondaki boşluk atılır, küçük harfe çevrilir.
 *
 * Normalize edilmeseydi `Ali@X.com` ile `ali@x.com` iki ayrı hesap açardı ve *"tek
 * e-posta tek hesap"* iş kuralı (CLOS-60) ihlal edilirdi. Kullanıcı da giriş yaparken
 * kayıt olduğu yazımı hatırlamak zorunda kalırdı — hatırlamaz.
 *
 * Normalizasyon SINIRDA yapılır ki kayıt, giriş ve şifre sıfırlama aynı değeri görsün;
 * veritabanındaki `lower(email)` unique indeksi de ikinci katman olarak durur.
 */
export const emailSchema = z
  .string({ error: "Geçerli bir e-posta adresi giriniz" })
  .trim()
  .toLowerCase()
  // ÖLÇÜLDÜ: normalizasyon doğrulamadan ÖNCE olmalı. Ters sırada `"  ali@x.com  "`
  // henüz kırpılmadığı için "geçersiz e-posta" sayılıyor ve kullanıcı, gözle
  // ayırt edemediği bir boşluk yüzünden reddediliyordu.
  .pipe(z.email("Geçerli bir e-posta adresi giriniz"));

export const registerSchema = z
  .object({
    name: z
      .string({ error: "Ad Soyad zorunludur" })
      .trim()
      .min(2, "Ad Soyad en az 2 karakter olmalıdır"),
    email: emailSchema,
    password: passwordSchema,

    /**
     * Kullanıcının kabul ettiği belge SÜRÜMLERİNİN kimlikleri (CLOS-342).
     *
     * **Neden boolean değil kimlik listesi:** `acceptTerms: true` "neyi kabul etti?"
     * sorusunu cevapsız bırakır. Metin değiştiğinde eski onay yeni metni kapsamaz; onay
     * ancak bir sürüme bağlıysa hukuken bir şey ifade eder.
     *
     * **Sunucu bunu DOĞRULAR:** listenin yürürlükteki zorunlu belgeleri tam olarak
     * kapsaması gerekir. İstemci eski bir sayfayı açık bırakmış olabilir; hangi metnin
     * yürürlükte olduğunu bilen taraf sunucudur.
     *
     * Varsayılan boş dizi: alanı hiç göndermeyen istemci de aynı alan hatasını alsın,
     * "alan eksik" ile "onay verilmedi" iki farklı mesaja bölünmesin.
     */
    acceptedDocumentIds: z
      .array(z.uuid("Onay bilgisi geçersiz"), { error: "Onay bilgisi geçersiz" })
      .default([]),
  })
  /**
   * Parola, e-postanın kullanıcı adı kısmını İÇEREMEZ.
   *
   * `ahmet@ornek.com` → `Ahmet12345` politikanın üç kuralını da geçer ama saldırganın
   * ilk deneyeceği paroladır. Uzunluk ve karakter çeşidi bunu yakalamaz; bu kural
   * yakalar ve maliyeti sıfırdır.
   */
  .superRefine((girdi, ctx) => {
    const kullaniciAdi = girdi.email.split("@")[0] ?? "";
    if (kullaniciAdi.length >= 3 && girdi.password.toLowerCase().includes(kullaniciAdi)) {
      ctx.addIssue({
        code: "custom",
        path: ["password"],
        message: "Şifre e-posta adresinizi içeremez",
      });
    }
  });
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string({ error: "Şifre zorunludur" }).min(1, "Şifre zorunludur"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z
    .string({ error: "Sıfırlama bağlantısı geçersiz" })
    .min(1, "Sıfırlama bağlantısı geçersiz"),
  // Yeni şifre kuralı register/reset ile AYNI (DRY): passwordSchema tekrar kullanılır
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * Sosyal giriş isteği (CLOS-336).
 *
 * Sağlayıcı KAPALI bir kümeden seçilir; serbest metin kabul edilseydi istemci
 * yapılandırılmamış ya da hiç var olmayan bir sağlayıcı adı gönderebilirdi.
 *
 * `callbackURL` bilerek YOKTUR: dönüş hedefini sunucu seçer, böylece açık yönlendirme
 * yüzeyi hiç doğmaz.
 */
export const socialSignInSchema = z.object({
  provider: z.literal("google", { error: "Desteklenmeyen giriş yöntemi" }),
});
export type SocialSignInInput = z.infer<typeof socialSignInSchema>;

/**
 * E-posta doğrulama (CLOS-339 `[S03]`).
 *
 * Token serbest metindir ama BOŞ olamaz: boş token'ı sorguya sokmak, "hiçbir şey"in
 * bir şeyle eşleşmesini ummak olurdu.
 */
export const verifyEmailSchema = z.object({
  token: z
    .string({ error: "Doğrulama bağlantısı geçersiz" })
    .min(1, "Doğrulama bağlantısı geçersiz"),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

/**
 * Doğrulama e-postasını yeniden gönderme (CLOS-338).
 *
 * Yanıt her durumda AYNIDIR: adres kayıtlı olsun olmasın, doğrulanmış olsun olmasın.
 * Farklı davransaydı bu uç bir hesap varlığı sorgulama aracına dönüşürdü.
 */
export const resendVerificationSchema = z.object({
  email: emailSchema,
});
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

/**
 * Yeniden onay (CLOS-343).
 *
 * Kayıt ucuyla AYNI alan adı kullanılıyor — iki farklı ad, iki farklı doğrulama yolu ve
 * zamanla ayrışan iki kural demekti.
 */
export const acceptConsentsSchema = z.object({
  acceptedDocumentIds: z
    .array(z.uuid("Onay bilgisi geçersiz"), { error: "Onay bilgisi geçersiz" })
    .default([]),
});
export type AcceptConsentsInput = z.infer<typeof acceptConsentsSchema>;

/**
 * İzin geri çekme (CLOS-343).
 *
 * Tek belge alır, liste değil: geri çekme kullanıcının tek tek ve bilinçli yaptığı bir
 * eylemdir; toplu bir listeyle "hepsini geri çek" demek, yanlışlıkla geri çekilen izinleri
 * kolaylaştırırdı.
 */
export const withdrawConsentSchema = z.object({
  documentId: z.uuid("Onay bilgisi geçersiz"),
});
export type WithdrawConsentInput = z.infer<typeof withdrawConsentSchema>;

/**
 * Profil güncelleme (CLOS-65 `[017]`).
 *
 * **E-posta alanı YOK — bilinçli.** Kart e-posta değişikliğinin yeniden doğrulama
 * gerektirdiğini söylüyor; o akış ayrı bir karttır (`[048]`). Buraya `email` eklemek,
 * doğrulama kapısı olmayan bir e-posta değişikliği yolu açmak olurdu.
 *
 * Her alan isteğe bağlı: gönderilmeyen alan DOKUNULMAMIŞ sayılır.
 *
 * **Şema BİÇİMİ doğrular, ZORUNLULUĞU değil.** Başta `name` alanına `min(1)` yazmıştım
 * ve bu iki enforcement noktası yaratıyordu: boş ad şemada takılıyor, sunucudaki alan
 * matrisine hiç ulaşmıyordu. Sonuç, matris kontrolünü KASTEN BOZDUĞUMDA hiçbir testin
 * düşmemesiydi — ölçtüm ve gördüm; dişsiz bir kontroldü.
 *
 * Ayrım artık net: biçim (uzunluk, desen, URL) şemada; "bu alan zorunlu mu?" matriste.
 * Matris konfigürasyondan gelir ve paylaşılan şema onu bilemez.
 */
export const updateProfileSchema = z.object({
  name: z.string().trim().max(120, "Ad Soyad çok uzun").optional(),
  title: z.string().trim().max(120, "Unvan çok uzun").nullable().optional(),
  phone: z
    .string()
    .trim()
    .max(32, "Telefon çok uzun")
    .regex(/^[0-9+()\s-]*$/, "Telefon yalnız rakam ve + ( ) - içerebilir")
    .nullable()
    .optional(),
  locale: z.string().trim().max(16, "Dil kodu çok uzun").nullable().optional(),
  timezone: z.string().trim().max(64, "Saat dilimi çok uzun").nullable().optional(),
  // Yalnız http(s): `z.url()` tek başına `javascript:`/`data:`/`vbscript:` şemalarını da
  // kabul ediyordu (denetim bulgusu) ve saklanan bu değer bir gün avatar `<img>`/`<a>`
  // ile render edilince depolanmış-XSS'e dönerdi. Protokol boot'ta değil, GİRDİDE kesilir.
  image: z
    .url({ protocol: /^https?$/, error: "Adres http(s) olmalıdır" })
    .max(2048, "Adres çok uzun")
    .nullable()
    .optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/**
 * 2FA etkinleştirme ve giriş ikinci adımı (CLOS-66 `[018]`).
 *
 * Kod 6 hane; boşluklara izin veriliyor çünkü authenticator uygulamaları kodu `123 456`
 * biçiminde gösteriyor ve kullanıcı aradaki boşlukla birlikte yapıştırıyor.
 */
export const twoFactorCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{3}\s?\d{3}$/, "Kod 6 haneli olmalıdır"),
});
export type TwoFactorCodeInput = z.infer<typeof twoFactorCodeSchema>;

/** Girişin ikinci adımı: meydan okuma token'ı + kod. */
export const twoFactorChallengeSchema = twoFactorCodeSchema.extend({
  challenge: z.string().min(1, "Doğrulama bilgisi eksik"),
});
export type TwoFactorChallengeInput = z.infer<typeof twoFactorChallengeSchema>;

/**
 * Kurtarma kodu (CLOS-67 `[019]`).
 *
 * Biçim `XXXXX-XXXXX` ama doğrulama GEVŞEK: kullanıcı kodu kâğıttan okuyup tireyi ya da
 * büyük/küçük harfi atlayabilir. Katı bir desen, doğru kodu elinde tutan kullanıcıyı
 * biçim yüzünden reddederdi — kurtarma akışında bu, hesabı kalıcı kaybetmek demektir.
 */
export const recoveryCodeSchema = z.object({
  recoveryCode: z
    .string()
    .trim()
    .min(10, "Kurtarma kodu geçersiz")
    .max(20, "Kurtarma kodu geçersiz"),
});
export type RecoveryCodeInput = z.infer<typeof recoveryCodeSchema>;

/** Girişin ikinci adımı, kurtarma koduyla. */
export const twoFactorRecoveryChallengeSchema = recoveryCodeSchema.extend({
  challenge: z.string().min(1, "Doğrulama bilgisi eksik"),
});
export type TwoFactorRecoveryChallengeInput = z.infer<typeof twoFactorRecoveryChallengeSchema>;

/**
 * 2FA devre dışı bırakma (CLOS-67 `[019]`).
 *
 * Üç şey birden isteniyor: parola (yeniden doğrulama), ikinci faktör (TOTP ya da kurtarma
 * kodu) ve AÇIK ONAY. Onay kutusu bir formalite değil — kullanıcı hesabının korumasını
 * kaldırdığını bilerek yapmalı.
 */
export const disableTwoFactorSchema = z
  .object({
    password: z.string().min(1, "Şifrenizi girin"),
    code: z.string().trim().optional(),
    recoveryCode: z.string().trim().optional(),
    confirm: z.literal(true, { error: "Devre dışı bırakmayı onaylamanız gerekiyor" }),
  })
  .refine((v) => Boolean(v.code) || Boolean(v.recoveryCode), {
    error: "Doğrulama kodu veya kurtarma kodu gerekli",
    path: ["code"],
  });
export type DisableTwoFactorInput = z.infer<typeof disableTwoFactorSchema>;

/** Kritik işlem öncesi parola yeniden doğrulaması (CLOS-67 `[019]`). */
export const reauthSchema = z.object({
  password: z.string().min(1, "Şifrenizi girin"),
});
export type ReauthInput = z.infer<typeof reauthSchema>;

/**
 * Parola + (2FA etkinse) ikinci faktör — yüksek etkili işlemlerin ortak girdisi
 * (CLOS-520 · CLOS-214 `[080]`).
 *
 * ⚠ İkinci faktör alanları ŞEMADA opsiyonel, SUNUCUDA koşullu. Şema "2FA açık mı?"
 * sorusunu bilemez; zorunlu yapsaydık 2FA kurmamış kullanıcı hiçbir yüksek etkili işlemi
 * yapamazdı. Zorunluluk `verifySecondFactor` içinde, hesabın gerçek durumuna bakılarak
 * uygulanıyor.
 */
export const stepUpFields = {
  code: z.string().trim().optional(),
  recoveryCode: z.string().trim().optional(),
};

export const stepUpSchema = reauthSchema.extend(stepUpFields);
export type StepUpInput = z.infer<typeof stepUpSchema>;

/**
 * Oturum içinden şifre değiştirme (CLOS-226 `[021]`).
 *
 * `currentPassword` HER ZAMAN isteniyor: yeniden doğrulama penceresi sıfır. Bir pencere
 * açmak ("son 5 dakikada giriş yaptıysan sormayalım") kullanıcıya birkaç saniye
 * kazandırırdı ama açık bırakılmış bir tarayıcı başındaki birine şifreyi değiştirme
 * imkânı verirdi — kazanç, riskin yanında hiç.
 *
 * `code`/`recoveryCode` yalnız 2FA etkinse gerekiyor; şema ikisini de isteğe bağlı
 * bırakıyor çünkü zorunluluk HESABIN DURUMUNA bağlı ve şema o durumu bilemez.
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Mevcut şifrenizi girin"),
  newPassword: passwordSchema,
  code: z.string().trim().optional(),
  recoveryCode: z.string().trim().optional(),
  revokeOtherSessions: z.boolean().optional(),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * İlk giriş onboarding durumu (CLOS-224 `[047]`).
 *
 * `skipped`, `completed`'dan AYRI: kullanıcı akışı atlayabilir ve bu meşru bir sonuçtur.
 * Tek bir "bitti" durumu, ürünün en değerli sinyalini — akışın gerçekten kullanılıp
 * kullanılmadığını — kaydın dışında bırakırdı.
 */
export const ONBOARDING_STATUSES = ["pending", "completed", "skipped"] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

/** Onboarding'i sonlandıran komut. Durum İSTEMCİDEN gelmiyor; eylem geliyor. */
export const onboardingActionSchema = z.object({
  action: z.enum(["complete", "skip"], { error: "Geçerli bir onboarding eylemi seçilmelidir" }),
});
export type OnboardingActionInput = z.infer<typeof onboardingActionSchema>;
